<template>
    <div
        :data-mui-theme="props.theme"
        class="mui-c-wrapper-with-shapes mui-background"
        :class="[
            `mui-c-wrapper-with-shapes--${props.direction}`,
            {
                'mui-enable-full-screen': props.enableFullScreen,
                'mui-scale-on-hover': props.enableScaleOnHover,
            },
        ]"
        @click="handleFullScreen"
    >
        <div class="mui-c-wrapper-with-shapes__wrapper__shape"></div>
        <div v-if="props.enableFullScreen" class="mui-c-wrapper-with-shapes__cta-full-screen">
            <VJoyIcon color="brand-primary" name="fullscren" size="xsmall" />
        </div>
        <div class="mui-c-wrapper-with-shapes__content">
            <slot />
        </div>
        <MuiDialogFullscreen ref="fullscreenDialog" :theme="props.theme" @dialog:after-hide="handleFullScreenClose">
            <VJoySpinner v-if="props.isFullscreenLoading" color="teal" />
            <slot name="fullscreen" />
        </MuiDialogFullscreen>
    </div>
</template>
<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {VJoyIcon, VJoySpinner} from '@maltjoy/core-vue';
    import type {MuiTheme} from '../../types/theme';
    import type {MuiDirection} from '../../types/ui';
    import MuiDialogFullscreen from '../DialogFullscreen.vue';

    type MuiWrapperWithShapesDirection = Extract<MuiDirection, 'bottom-left' | 'bottom-right'>;

    const props = withDefaults(
        defineProps<{
            theme?: MuiTheme;
            direction?: MuiWrapperWithShapesDirection;
            enableFullScreen?: boolean;
            enableScaleOnHover?: boolean;
            isFullscreenLoading?: boolean;
        }>(),
        {
            direction: 'bottom-left',
            enableFullScreen: false,
            enableScaleOnHover: false,
            isFullscreenLoading: false,
        },
    );

    const emit = defineEmits<{
        (e: 'fullscreen:hide'): void;
    }>();

    const {handleFullScreen, handleFullScreenClose} = fullScreen();

    function fullScreen() {
        const fullscreenDialogRef = useTemplateRef<InstanceType<typeof MuiDialogFullscreen>>('fullscreenDialog');

        function handleFullScreen() {
            if (props.enableFullScreen) {
                fullscreenDialogRef.value?.show();
            }
        }

        function handleFullScreenClose() {
            emit('fullscreen:hide');
        }

        return {
            handleFullScreen,
            handleFullScreenClose,
        };
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .mui-c-wrapper-with-shapes {
        width: 100%;
        aspect-ratio: 10 / 6;

        @include mq.screen_xxs(min) {
            aspect-ratio: none;
            height: 200px;
        }

        @include mq.screen_sm(min) {
            height: 250px;
        }

        @include mq.screen_md(min) {
            height: 550px;
        }

        position: relative;
        border-radius: var(--joy-core-radius-4);
        padding: var(--joy-core-spacing-7) var(--joy-core-spacing-6) 0 var(--joy-core-spacing-6);
        overflow: hidden;

        @include mq.screen_md(min) {
            aspect-ratio: 1 / 1;
            padding: var(--joy-core-spacing-11) var(--joy-core-spacing-9);
        }

        &__wrapper {
            position: absolute;
            inset: 0;

            &__shape {
                pointer-events: none;
                position: absolute;
                border-radius: 999px;
                background: var(--mui-color-shape);
                transform-origin: center right;
                transform: rotate(-25deg);

                top: -50px;
                right: 35px;
                height: 325px;
                width: 200%;

                @include mq.screen_sm(min) {
                    top: -85px;
                    right: 50px;
                    height: 500px;
                }

                @include mq.screen_md(min) {
                    top: -165px;
                    right: 105px;
                    height: 550px;
                    transform: rotate(-45deg);
                }
            }
        }

        &__content {
            position: relative;
            width: 100%;
            height: 100%;

            @include mq.screen_md(min) {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
            }

            & > img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                border-radius: var(--joy-core-radius-3);
                margin: 0 auto;
                display: block;
            }
        }

        &__cta-full-screen {
            display: none;
        }

        &.mui-enable-full-screen {
            .mui-c-wrapper-with-shapes__cta-full-screen {
                display: block;
                position: absolute;
                top: var(--joy-core-spacing-2);
                right: var(--joy-core-spacing-2);
                border: 1px solid var(--mui-color-background-darker);
                border-radius: var(--joy-core-radius-3);
                padding: var(--joy-core-spacing-1);
                line-height: 0;
                z-index: 2;

                .joy-icon {
                    --icon-size: 12px;
                    margin: 0;
                    padding: 0;
                    color: var(--mui-color-background-darker);

                    * {
                        transition: color 0.3s ease;
                    }
                }

                @include mq.screen_md(min) {
                    .joy-icon {
                        --icon-size: 16px;
                    }
                }
            }

            &:hover {
                cursor: pointer;

                .mui-c-wrapper-with-shapes__cta-full-screen {
                    background-color: var(--mui-color-background-darker);

                    .joy-icon * {
                        color: var(--joy-color-neutral-0);
                    }
                }
            }
        }

        &.mui-scale-on-hover {
            .mui-c-wrapper-with-shapes__content {
                transition: transform 0.3s ease;
            }

            &:hover {
                .mui-c-wrapper-with-shapes__content {
                    transform: scale(1.025);
                }
            }
        }
    }
</style>
